import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import CustomButton from "../../pages/CustomButton";
import { BASE_URL } from "../../config";
import { getAuthHeaders } from "../../services/useAuth";

export default function LocationHistoryMockTable() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [employeeName, setEmployeeName] = useState("");
  const [employeeId, setEmployeeId] = useState(null);

  const [workingDate, setWorkingDate] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [actionLoadingIndex, setActionLoadingIndex] = useState(null); 

  const tableRef = useRef(null); 

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const employeeId = params.get("employeeId");
    const working_date = params.get("working_date");
    const retailerId = params.get("retailerId");
    const retailerType = params.get("retailerType");

    console.log("URL Params:", {
      employeeId,
      working_date,
      retailerId,
      retailerType,
    });

    if (employeeId && working_date) {
      setWorkingDate(working_date);
      fetchLocationReport(employeeId, working_date);
    } else if (employeeId && retailerId && retailerType) {
      fetchVisitHistory(employeeId, retailerId, retailerType);
    } else {
  console.warn("Missing parameters, showing empty data");
  setRows([]);
  setTotalRecords(0);
  setLoading(false);
}
  }, []);

  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return "";
    try {
      const date = new Date(dateTimeStr);
      if (isNaN(date.getTime())) return dateTimeStr;

      // If string is just yyyy-mm-dd, show date only
      if (dateTimeStr.includes("T") === false && dateTimeStr.length === 10) {
        const day = String(date.getDate()).padStart(2, "0");
        const month = date.toLocaleString("en", { month: "short" });
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
      }

      const day = String(date.getDate()).padStart(2, "0");
      const month = date.toLocaleString("en", { month: "short" });
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${day} ${month} ${year} ${hours}:${minutes}`;
    } catch (e) {
      return dateTimeStr;
    }
  };

  const fetchVisitHistory = async (employeeId, retailerId, retailerType) => {
    try {
      setLoading(true);

      const parsedEmployeeId = parseInt(employeeId, 10);
      const parsedRetailerId = parseInt(retailerId, 10);

      if (isNaN(parsedEmployeeId)) {
        throw new Error(`Invalid employee ID: ${employeeId}`);
      }
      if (isNaN(parsedRetailerId)) {
        throw new Error(`Invalid retailer ID: ${retailerId}`);
      }

      const normalizedRetailerType = retailerType.toLowerCase();

      const apiUrl = `${BASE_URL}/api/visit-history/${parsedEmployeeId}/${parsedRetailerId}/?retailer_type=${encodeURIComponent(
        normalizedRetailerType
      )}`;

      console.log("Fetching visit history from:", apiUrl);

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log("API Response:", data);
      processVisitHistoryData(data);

      setLoading(false);
    } catch (err) {
      console.error("Error fetching visit history:", err);
      setError(err.message || String(err));
      setLoading(false);
    }
  };

  const processVisitHistoryData = (data) => {
    if (!data) return;

    if (data.retailer_name) {
      setEmployeeName(data.retailer_name);
    }

    if (data.visit_history && Array.isArray(data.visit_history) && data.visit_history.length > 0) {
      setTotalRecords(data.total_visits || data.visit_history.length);

      const processedRows = data.visit_history.map((visit, index) => ({
        sno: visit.s_no || index + 1,
        type: visit.type || "",
        code: visit.visit_id || visit.code || `VISIT_${index + 1}`,
        client: visit.client_name || data.retailer_name || "",
        clientId: data.retailer_id || visit.retailer_id,
        pob:
          visit.pob !== undefined && visit.pob !== null
            ? visit.pob
            : visit.pob === 0
            ? 0
            : 0,
        city: visit.city || "",
        area: visit.area || "",
        geoCodeDate: formatDateTime(visit.geocode_date),
        workingDate: formatDateTime(visit.working_date),
        serverTime: visit.server_time || "",
        distance:
          visit.distance_meters != null && !isNaN(Number(visit.distance_meters))
            ? Number(visit.distance_meters).toFixed(2)
            : "0.00",
        clientLatitude: visit.client_latitude || "",
        clientLongitude: visit.client_longitude || "",
        clientAddress: visit.client_address || "",
        employeeLocationTime: formatDateTime(
          visit.employee_location_time || visit.employee_location_time
        ),
        mock: visit.is_mock || false,
        employeeId: visit.employee_id || visit.emp_id || null,
        employeeLatitude: visit.employee_latitude || "",
        employeeLongitude: visit.employee_longitude || "",
        employeeLocationAddress: visit.employee_address || "",
        removeGeocodeFlag: visit.remove_geocode_flag || false,
        freezeGeoCodeFlag: visit.freeze_geocode_flag || false,
        distanceKm: "0.00",
        kmCovered: "0.00",
      }));

      setRows(processedRows);
    } else {
      setRows([]);
      setTotalRecords(0);
    }
  };

  const fetchLocationReport = async (employeeId, trackDate) => {
    try {
      setLoading(true);

      const formattedDate = formatDateForAPI(trackDate);

      const response = await fetch(
        `${BASE_URL}/api/employee/${employeeId}/location-report-tp?track_date=${formattedDate}`,
        {
          method: "GET",
          headers: getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const data = await response.json();
      processLocationData(data);

      setLoading(false);
    } catch (err) {
      console.error("Error fetching location report:", err);
      setError(err.message || String(err));
      setLoading(false);
    }
  };

  const formatDateForAPI = (dateStr) => {
    if (!dateStr) return dateStr;
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateStr;
    }

    try {
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    } catch (e) {
      return dateStr;
    }
  };

  const processLocationData = (data) => {
    if (!data) return;

    if (data.employee) {
      setEmployeeName(data.employee.name || "");
    }

    // For this view we do not render location report detail; just clear rows
    setRows([]);
    setTotalRecords(0);
  };

  // navigation: try employee coords first, then client coords
  const handleEmployeeTimeClick = (row) => {
    const lat = row.employeeLatitude || row.clientLatitude;
    const lng = row.employeeLongitude || row.clientLongitude;

    if (!lat || !lng) {
      alert("No coordinates available to navigate for this record.");
      return;
    }

    const empFromRow =
      row.employeeId || row.employee_id || row.emp_id || row.empId || null;

    const urlParams = new URLSearchParams(window.location.search);
    const empFromUrl =
      urlParams.get("employee_id") || urlParams.get("employeeId") || null;

    const empId = empFromRow || employeeId || empFromUrl;

    if (!empId) {
      console.error("no employee id found", row, window.location.search);
      alert(
        "Missing employee id for this record. Cannot open detailed history."
      );
      return;
    }

    const rawDate =
      workingDate ||
      row.workingDate ||
      row.working_date ||
      row.employeeLocationTime ||
      row.employee_location_time ||
      "";

    const trackDate = rawDate ? formatDateForAPI(rawDate) : "";

    if (trackDate)
      sessionStorage.setItem("location_history_track_date", trackDate);
    sessionStorage.setItem("location_history_employee_id", String(empId));

    const params = new URLSearchParams();
    params.set("lat", lat);
    params.set("lng", lng);

    if (row.employeeLocationTime) params.set("time", row.employeeLocationTime);
    if (row.client) params.set("client", row.client);
    if (row.type) params.set("retailer_type", row.type);
    if (row.clientId) params.set("retailer_id", String(row.clientId));
    const url = `/reporting/location-history?${params.toString()}`;
    window.open(url, "_blank", "width=1400,height=700");
    navigate(url);
  };

  // ----- Remove geocode -----
  const handleRemove = async (idx) => {
    try {
      const row = rows[idx];
      if (!row) return;

      const confirmed = window.confirm(`Remove geocode for ${row.client}?`);
      if (!confirmed) return;

      const retailerId = row.clientId;
      if (!retailerId) {
        alert("Missing retailer id for this row.");
        return;
      }

      setActionLoadingIndex(idx);

      const params = new URLSearchParams();
      if (row.clientLatitude) params.set("latitude", row.clientLatitude);
      if (row.clientLongitude) params.set("longitude", row.clientLongitude);
      if (row.type) params.set("retailer_type", row.type);

      const url = `${BASE_URL}/api/employee/location/${encodeURIComponent(
        retailerId
      )}${params.toString() ? `?${params.toString()}` : ""}`;

      const response = await fetch(url, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Remove API Error: ${response.status} - ${text}`);
      }

      const updated = [...rows];
      updated[idx] = { ...updated[idx], removeGeocodeFlag: true };
      setRows(updated);

      alert("Geocode removed successfully.");
    } catch (err) {
      console.error("Error removing geocode:", err);
      alert(`Failed to remove geocode: ${err.message || err}`);
    } finally {
      setActionLoadingIndex(null);
    }
  };

  // ----- Freeze geocode (one-way only) -----
  const handleToggleFreeze = async (idx) => {
    try {
      const row = rows[idx];
      if (!row) return;

      if (row.freezeGeoCodeFlag) {
        alert(
          "This record is already frozen and cannot be unfrozen from here."
        );
        return;
      }

      const confirmed = window.confirm(
        `Freeze geocode for ${row.client}? This action is one-way from the UI.`
      );
      if (!confirmed) return;

      const retailerId = row.clientId;
      if (!retailerId) {
        alert("Missing retailer id for this row.");
        return;
      }

      setActionLoadingIndex(idx);

      const params = new URLSearchParams();
      if (row.type) params.set("retailer_type", row.type);
      if (row.clientLatitude) params.set("latitude", row.clientLatitude);
      if (row.clientLongitude) params.set("longitude", row.clientLongitude);

      const url = `${BASE_URL}/api/freeze-location/${encodeURIComponent(
        retailerId
      )}${params.toString() ? `?${params.toString()}` : ""}`;

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Freeze API Error: ${response.status} - ${text}`);
      }

      const updated = [...rows];
      updated[idx] = { ...updated[idx], freezeGeoCodeFlag: true };
      setRows(updated);

      alert("Geocode frozen. This UI does not support unfreeze.");
    } catch (err) {
      console.error("Error toggling freeze:", err);
      alert(`Failed to freeze geocode: ${err.message || err}`);
    } finally {
      setActionLoadingIndex(null);
    }
  };

  // ---------------------- PRINT / EXPORT HANDLERS ----------------------

  // Try to gather all same-origin CSS rules so the print window looks like original.
  const collectStylesForPrint = () => {
    let css = "";
    for (let i = 0; i < document.styleSheets.length; i++) {
      const sheet = document.styleSheets[i];
      try {
        const rules = sheet.cssRules;
        if (!rules) continue;
        for (let j = 0; j < rules.length; j++) {
          const rule = rules[j];
          try {
            css += rule.cssText + "\n";
          } catch (e) {
            // ignore unreadable/cross-origin rules
          }
        }
      } catch (e) {
        // cross-origin or inaccessible stylesheet - skip
        continue;
      }
    }
    return css;
  };

  const handlePrint = () => {
    const tableEl = tableRef.current;
    if (!tableEl) {
      alert("Table not found to print.");
      return;
    }
    const tableHtml = tableEl.outerHTML;

    const styles = collectStylesForPrint();

    const html = `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Print - Location History</title>
          <style>
            ${styles}
            /* fallback print styles to ensure visibility */
            @page { size: landscape; margin: 10mm; }
            body { font-family: Arial, Helvetica, sans-serif; color: #111; padding: 10px; }
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 6px; vertical-align: top; text-align: left; word-break: break-word; }
            thead th { position: sticky; top: 0; background: #066; color: #fff; z-index: 2; }
            /* ensure long tables paginate properly */
            tr { page-break-inside: avoid; }
            @media print {
              thead { display: table-header-group; }
            }
          </style>
        </head>
        <body>
          <div>
            <h3>Location Time (${employeeName || "Employee"} - ${
      workingDate || "Visit History"
    })</h3>
            <div>Generated on: ${new Date().toLocaleString()}</div>
          </div>
          <div style="margin-top:10px;">
            ${tableHtml}
          </div>
          <script>
            window.onload = function() {
              setTimeout(function() { window.print(); }, 250);
            }
          </script>
        </body>
      </html>
    `;

    const printWindow = window.open("", "_blank", "width=1200,height=800");
    if (!printWindow) {
      alert("Unable to open print window. Please allow popups for this site.");
      return;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  // Export current visible table to CSV preserving header order and visible cells
  const handleExportExcel = () => {
    const tableEl = tableRef.current;
    if (!tableEl) {
      alert("Table not found to export.");
      return;
    }

    try {
      // collect headers
      const headers = [];
      const ths = tableEl.querySelectorAll("thead th");
      ths.forEach((th) => {
        // use visible text
        headers.push((th.textContent || th.innerText || "").trim());
      });

      // collect rows
      const rowsData = [];
      const trs = tableEl.querySelectorAll("tbody tr");
      trs.forEach((tr) => {
        const cells = [];
        const tds = tr.querySelectorAll("td");
        // if row is placeholder (no data), skip
        if (tds.length === 0) return;
        tds.forEach((td) => {
          // preserve line breaks inside cells
          const txt = (td.innerText || td.textContent || "")
            .replace(/\n+/g, " ")
            .trim();
          cells.push(`"${txt.replace(/"/g, '""')}"`);
        });
        rowsData.push(cells.join(","));
      });

      if (rowsData.length === 0) {
        alert("No rows to export");
        return;
      }

      const csvContent = [
        headers.map((h) => `"${(h || "").replace(/"/g, '""')}"`).join(","),
        ...rowsData,
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const filename = `location_history_${(employeeName || "report").replace(
        /\s+/g,
        "_"
      )}.csv`;

      if (navigator.msSaveBlob) {
        navigator.msSaveBlob(blob, filename);
      } else {
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error("Export failed:", err);
      alert("Export failed: " + (err.message || err));
    }
  };

  if (loading) {
    return (
      <div className="bg-white p-1">
        <div className="flex items-center justify-center">
          <div className="text-gray-600 text-lg">Loading data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white p-1">
        <div className="flex items-center justify-center">
          <div className="text-red-600 text-lg">Error: {error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-2">
      {/* hide scrollbars but allow scrolling */}
      <style>{`
        /* Scrollbar hiding for the table container */
        .scrollable-table {
          overflow: auto;
          -webkit-overflow-scrolling: touch; /* smooth scrolling on iOS */
        }
        /* Firefox */
        .scrollable-table {
          scrollbar-width: none;
        }
        /* IE 10+ */
        .scrollable-table {
          -ms-overflow-style: none;
        }
        /* Webkit browsers */
        .scrollable-table::-webkit-scrollbar {
          display: none;
          width: 0;
          height: 0;
        }
      `}</style>

      <div className="mb-4 p-3 bg-gray-50 border">
        <h2 className="text-sm font-bold text-gray-800">
          Location Time ({employeeName || "Employee"} -{" "}
          {workingDate || "Visit History"})
        </h2>
      </div>

      <div className=" border border-gray-300 ">
        <table ref={tableRef} className="w-full text-xs border-collapse">
          <thead className="bg-cyan-600 text-white sticky top-0 z-10">
            <tr>
              <th className="border border-gray-300 px-2 py-2 text-center font-semibold">
                S.No
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center font-semibold">
                Type
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center font-semibold">
                Code
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center font-semibold">
                Client
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center font-semibold">
                POB
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center font-semibold">
                City
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center font-semibold">
                Area
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center font-semibold">
                Geo Code Date
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center font-semibold">
                Working Date
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center font-semibold">
                Server Time
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center font-semibold">
                Distance (m)
              </th>

              <th className="border border-gray-300 px-2 py-2 text-center font-semibold">
                Client Address
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center font-semibold">
                Employee Location Time
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center font-semibold">
                Mock
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center font-semibold">
                Employee Address
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center font-semibold">
                Remove Geocode
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center font-semibold">
                Freeze Geo Code
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center font-semibold">
                Distance (K.M.)
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center font-semibold">
                K.M. Covered
              </th>
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={21}
                  className="border border-gray-300 px-2 py-4 text-center text-gray-500"
                >
                  No data available
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr
                  key={idx}
                  className={`align-top ${
                    idx % 2 === 0 ? "bg-white" : "bg-purple-50"
                  }`}
                >
                  <td className="border border-gray-300 px-2 py-2 text-center">
                    {row.sno}
                  </td>
                  <td className="border border-gray-300 px-2 py-2 text-center bg-green-100 font-medium">
                    {row.type}
                  </td>
                  <td className="border border-gray-300 px-2 py-2 text-center bg-green-100">
                    {row.code}
                  </td>
                  <td className="border border-gray-300 px-2 py-2">
                    {row.client}
                  </td>
                  <td className="border border-gray-300 px-2 py-2 text-center">
                    {row.pob}
                  </td>
                  <td className="border border-gray-300 px-2 py-2 text-center">
                    {row.city}
                  </td>
                  <td className="border border-gray-300 px-2 py-2 text-center">
                    {row.area}
                  </td>
                  <td className="border border-gray-300 px-2 py-2 text-center">
                    {row.geoCodeDate}
                  </td>
                  <td className="border border-gray-300 px-2 py-2 text-center">
                    {row.workingDate}
                  </td>
                  <td className="border border-gray-300 px-2 py-2 text-center">
                    {row.serverTime}
                  </td>
                  <td className="border border-gray-300 px-2 py-2 text-right font-semibold bg-pink-100">
                    {row.distance}
                  </td>

                  <td className="border border-gray-300 px-2 py-2 max-w-xs">
                    <div className="text-xs leading-tight">
                      {row.clientAddress}
                    </div>
                  </td>
                  <td className="border border-gray-300 px-2 py-2 text-center">
                    <div
                      className={`cursor-pointer ${
                        row.employeeLatitude || row.clientLatitude
                          ? "text-blue-600 underline hover:text-blue-800"
                          : "text-gray-500"
                      }`}
                      onClick={() => handleEmployeeTimeClick(row)}
                    >
                      {row.employeeLocationTime || "—"}
                    </div>
                  </td>
                  <td className="border border-gray-300 px-2 py-2 text-center text-green-600 font-medium">
                    {String(row.mock)}
                  </td>
                  <td className="border border-gray-300 px-2 py-2 max-w-xs">
                    <div className="text-xs leading-tight">
                      {row.employeeLocationAddress}
                    </div>
                  </td>

                  <td className="border border-gray-300 px-2 py-2 text-center">
                    <CustomButton
                      onClick={() => handleRemove(idx)}
                      className={`text-white ${
                        row.removeGeocodeFlag
                          ? "bg-gray-400"
                          : "bg-red-600 hover:bg-red-700"
                      } border-red-700`}
                      disabled={
                        actionLoadingIndex === idx || row.removeGeocodeFlag
                      }
                    >
                      {actionLoadingIndex === idx
                        ? "Processing..."
                        : row.removeGeocodeFlag
                        ? "Removed"
                        : "Remove"}
                    </CustomButton>
                  </td>

                  <td className="border border-gray-300 px-2 py-2 text-center">
                    <CustomButton
                      onClick={() => handleToggleFreeze(idx)}
                      className={`text-white ${
                        row.freezeGeoCodeFlag
                          ? "bg-gray-400"
                          : "bg-blue-600 hover:bg-blue-700"
                      }`}
                      disabled={
                        actionLoadingIndex === idx || row.freezeGeoCodeFlag
                      }
                    >
                      {actionLoadingIndex === idx
                        ? "Processing..."
                        : row.freezeGeoCodeFlag
                        ? "Frozen"
                        : "Freeze"}
                    </CustomButton>
                  </td>

                  <td className="border border-gray-300 px-2 py-2 text-center">
                    {row.distanceKm}
                  </td>

                  <td className="border border-gray-300 px-2 py-2 text-center font-semibold">
                    {row.kmCovered}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile: stacked card list (no visible horizontal scrollbar) */}
      <div className="block md:hidden">
        {rows.length === 0 ? (
          <div className="border border-gray-300 p-3 text-center text-gray-500">
            No data available
          </div>
        ) : (
          rows.map((row, idx) => (
            <div
              key={idx}
              className=" border-gray-200 mb-3 p-3 bg-white shadow-sm"
            >
              <div className="flex justify-between items-start">
                <div>
                  <div className="text-sm font-semibold">
                    {row.client || `Row ${row.sno}`}
                  </div>
                  <div className="text-xs text-gray-600">
                    {row.type} • Code: {row.code}
                  </div>
                </div>
                <div className="text-right text-xs">
                  <div className="font-semibold">{row.pob}</div>
                  <div className="text-gray-500">POB</div>
                </div>
              </div>

              <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                <div>
                  <div className="text-gray-600">City</div>
                  <div className="font-medium">{row.city || "—"}</div>
                </div>
                <div>
                  <div className="text-gray-600">Area</div>
                  <div className="font-medium">{row.area || "—"}</div>
                </div>

                <div>
                  <div className="text-gray-600">Geo Code Date</div>
                  <div className="font-medium">{row.geoCodeDate || "—"}</div>
                </div>
                <div>
                  <div className="text-gray-600">Working Date</div>
                  <div className="font-medium">{row.workingDate || "—"}</div>
                </div>

                <div>
                  <div className="text-gray-600">Employee Time</div>
                  <div
                    className={`text-sm ${
                      row.employeeLatitude || row.clientLatitude
                        ? "text-blue-600 underline cursor-pointer"
                        : "text-gray-500"
                    }`}
                    onClick={() =>
                      (row.employeeLatitude || row.clientLatitude) &&
                      handleEmployeeTimeClick(row)
                    }
                  >
                    {row.employeeLocationTime || "—"}
                  </div>
                </div>
                <div>
                  <div className="text-gray-600">Distance (m)</div>
                  <div className="font-medium">{row.distance}</div>
                </div>

                <div className="col-span-2 mt-1">
                  <div className="text-gray-600 text-xs">Client Address</div>
                  <div className="text-xs leading-tight">
                    {row.clientAddress || "—"}
                  </div>
                </div>

                <div className="col-span-2 mt-2 flex gap-2">
                  <CustomButton
                    onClick={() => handleRemove(idx)}
                    className={`text-white text-xs ${
                      row.removeGeocodeFlag
                        ? "bg-gray-400"
                        : "bg-red-600 hover:bg-red-700"
                    } px-2 py-1`}
                    disabled={
                      actionLoadingIndex === idx || row.removeGeocodeFlag
                    }
                  >
                    {actionLoadingIndex === idx
                      ? "Processing..."
                      : row.removeGeocodeFlag
                      ? "Removed"
                      : "Remove"}
                  </CustomButton>

                  <CustomButton
                    onClick={() => handleToggleFreeze(idx)}
                    className={`text-white text-xs ${
                      row.freezeGeoCodeFlag
                        ? "bg-gray-400"
                        : "bg-blue-600 hover:bg-blue-700"
                    } px-2 py-1`}
                    disabled={
                      actionLoadingIndex === idx || row.freezeGeoCodeFlag
                    }
                  >
                    {actionLoadingIndex === idx
                      ? "Processing..."
                      : row.freezeGeoCodeFlag
                      ? "Frozen"
                      : "Freeze"}
                  </CustomButton>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Print / Export buttons: wired to handlers that use the tableRef above */}
      <div className="mt-4 flex gap-4 justify-center">
        <CustomButton onClick={handlePrint} label="Print" variant="print">
          🖨
        </CustomButton>
        <CustomButton
          onClick={handleExportExcel}
          label="Export"
          variant="export"
        >
          📥
        </CustomButton>
      </div>

      <div className="mt-3 text-right">
        <div className="text-sm font-semibold text-gray-700">
          Total Records: {totalRecords}
        </div>
      </div>
    </div>
  );
}
